import { useEffect, useState } from "react";
import { api } from "../api";
import { PageSection, Pagination, ReportTable, StatusBadge } from "../components/reporting";
import type { Column } from "../components/reporting";

interface PausedDestination {
  id: string;
  tenantId: string;
  tenantName: string;
  label: string;
  kind: "sales_email" | "webhook";
  consecutive_failures: number;
  paused_reason: string | null;
  updated_at: string;
  held: number;
}

interface Failure {
  id: string;
  tenantId: string;
  tenantName: string;
  destination_id: string;
  destination: string;
  kind: "sales_email" | "webhook";
  status: string;
  response_code: number | null;
  attempted_at: string;
}

interface DeliveryAnomaly {
  tenantId: string;
  tenantName: string;
  volumeSpike: boolean;
  configurationSpike: boolean;
  sentCurrentHour: number;
  baselineHourly: number;
  destinationChanges: number;
}

const columns: Column[] = [
  { label: "Tenant", align: "left", width: "220px" },
  { label: "Destination", align: "left", width: "220px" },
  { label: "Channel", align: "left", width: "130px" },
  { label: "Status", align: "left", width: "130px" },
  { label: "Response", align: "left", width: "130px" },
  { label: "When", align: "left" },
];

export function AdminDeliveryHealth() {
  const [paused, setPaused] = useState<PausedDestination[]>([]);
  const [failures, setFailures] = useState<Failure[]>([]);
  const [anomalies, setAnomalies] = useState<DeliveryAnomaly[]>([]);
  const [error, setError] = useState("");

  const load = async () => {
    const response = await api<{
      paused: PausedDestination[];
      failures: Failure[];
      anomalies: DeliveryAnomaly[];
    }>("/api/admin/delivery-health");
    setPaused(response.paused);
    setFailures(response.failures);
    setAnomalies(response.anomalies);
  };
  useEffect(() => {
    void load().catch((problem) =>
      setError(problem instanceof Error ? problem.message : "Delivery health unavailable"),
    );
  }, []);

  const resume = async (destination: PausedDestination, replayHeld: boolean) => {
    const password = window.prompt("Confirm your FMC admin password");
    if (!password) return;
    const reauth = await api<{ token: string }>("/api/reauthenticate", {
      method: "POST",
      body: JSON.stringify({ password, action: "destination.change" }),
    });
    await api(`/api/admin/destinations/${encodeURIComponent(destination.id)}/resume`, {
      method: "POST",
      body: JSON.stringify({
        tenantId: destination.tenantId,
        replayHeld,
        reauthenticationToken: reauth.token,
      }),
    });
    await load();
  };

  const rows = failures.map((failure) => [
    <span className="text-body text-primary">{failure.tenantName}</span>,
    <span>
      <span className="block text-body text-secondary">{failure.destination}</span>
      <span className="block font-mono text-micro text-tertiary">{failure.destination_id}</span>
    </span>,
    <span className="text-caption text-secondary">
      {failure.kind === "sales_email" ? "Sales email" : "Webhook"}
    </span>,
    <StatusBadge label={failure.status} tone={failure.status === "retry" ? "amber" : "red"} />,
    <span className="font-mono text-caption text-tertiary">
      {failure.response_code ? `HTTP ${failure.response_code}` : "No body retained"}
    </span>,
    <span className="text-body text-secondary">
      {new Date(failure.attempted_at).toLocaleString("en-GB")}
    </span>,
  ]);
  const held = paused.reduce((total, destination) => total + destination.held, 0);

  return (
    <div className="flex flex-col gap-[24px]">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { value: failures.length, label: "Failed or retrying" },
          { value: paused.length, label: "Paused destinations" },
          { value: held, label: "Held jobs" },
          { value: anomalies.length, label: "Anomaly signals" },
        ].map((metric) => (
          <div
            key={metric.label}
            className="bg-surface-white border border-default rounded-xl px-[22px] py-[18px]"
          >
            <div className="text-caption text-tertiary">{metric.label}</div>
            <div className="text-stat font-bold text-primary mt-2">{metric.value}</div>
          </div>
        ))}
      </div>

      {anomalies.length > 0 && (
        <PageSection
          title="Non-PII anomaly signals"
          right={<StatusBadge label="Review required" tone="amber" />}
        >
          <div className="flex flex-col gap-3">
            {anomalies.map((anomaly) => (
              <div
                key={anomaly.tenantId}
                className="rounded-md border border-status-amber/40 bg-[rgba(255,184,0,0.08)] px-4 py-3"
              >
                <div className="text-body font-semibold text-primary">{anomaly.tenantName}</div>
                <div className="text-caption text-secondary mt-1">
                  {anomaly.volumeSpike
                    ? `${anomaly.sentCurrentHour} sends this hour versus ${anomaly.baselineHourly.toFixed(1)} hourly baseline. `
                    : ""}
                  {anomaly.configurationSpike
                    ? `${anomaly.destinationChanges} recipient/destination changes in the last hour.`
                    : ""}
                </div>
              </div>
            ))}
          </div>
        </PageSection>
      )}

      {paused.length > 0 && (
        <PageSection
          title="Paused destinations"
          right={<StatusBadge label="Needs operator action" tone="red" />}
        >
          <div className="flex flex-col gap-3">
            {paused.map((destination) => (
              <div
                key={`${destination.tenantId}:${destination.id}`}
                className="rounded-md border border-status-red/40 bg-[rgba(255,101,80,0.08)] px-4 py-3.5 flex items-center justify-between gap-4"
              >
                <div>
                  <div className="text-body font-semibold text-primary">
                    {destination.label} · {destination.tenantName}
                  </div>
                  <div className="text-caption text-secondary mt-1">
                    {destination.paused_reason} · {destination.consecutive_failures} consecutive
                    failures · {destination.held} held jobs
                  </div>
                  <div className="font-mono text-micro text-tertiary mt-1">{destination.id}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      void resume(destination, true).catch((problem) =>
                        setError(problem instanceof Error ? problem.message : "Resume failed"),
                      )
                    }
                    className="h-9 px-3 rounded-xs bg-brand-red text-on-brand text-caption font-semibold"
                  >
                    Resume & replay
                  </button>
                  <button
                    onClick={() =>
                      void resume(destination, false).catch((problem) =>
                        setError(problem instanceof Error ? problem.message : "Resume failed"),
                      )
                    }
                    className="h-9 px-3 rounded-xs border border-default bg-surface-white text-caption font-semibold"
                  >
                    Resume only
                  </button>
                </div>
              </div>
            ))}
          </div>
        </PageSection>
      )}

      <PageSection
        title="Recent failed attempts"
        right={
          <span className="text-caption text-tertiary">
            Status codes and opaque identifiers only; no lead or recipient PII
          </span>
        }
        bodyClassName="px-[20px] pt-[16px] pb-[20px]"
      >
        {error ? (
          <p className="py-10 text-center text-status-red">{error}</p>
        ) : rows.length ? (
          <>
            <ReportTable columns={columns} rows={rows} />
            <Pagination from={1} to={rows.length} total={rows.length} />
          </>
        ) : (
          <p className="py-10 text-center text-body text-tertiary">No failed deliveries.</p>
        )}
      </PageSection>
    </div>
  );
}
